/**
 * Add one team member: who they are, which clinics they can see, their role
 * and clinic roles, their clinical titles, how they are employed and their
 * Roger App account.
 *
 * The role is a shortcut: Field clinician and Clinical supervisor send their
 * preset, which grants the agency's configured roles and pre-checks the clinic
 * roles that go with it; Custom picks agency roles by hand. The clinic roles
 * stay editable either way, and anything finer is set in the User Manager.
 */

import React, { useEffect, useMemo } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  MultiSelect,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { AlertTriangle, ChevronDown, Info, Loader2 } from 'lucide-react';
import type { OnboardRogerDocMode, UserArchetype } from '../../../services/api';
import type { EmployeeType } from '../../../services/api/models/EmployeeType';
import type { UserType } from '../../../services/api/models/UserType';
import { EMPLOYEE_TYPE_OPTIONS } from '../../../constants/employeeTypes';
import { USER_TYPE_OPTIONS } from '../../../constants/userTypes';
import { disciplineProse } from '../../../constants/disciplines';
import type { AgencyClinicResponse } from '../types';
import {
  useIsAgencyRogerEmrEnabled,
  useShowEmrUserManagement,
} from '../../../hooks/useShowEmrUserManagement';
import { filterAssignableAgencyRoles } from '../../../utils/rbacRoles';
import { CLINIC_ROLE_OPTIONS, CLINIC_ROLE_SLUGS } from '../grid/clinicSetup';
import { useOnboardRoleContext } from './useOnboardRoleContext';
import {
  useOnboardUser,
  type ExistingAppAccount,
  type RoleChoice,
} from './useOnboardUser';

const ROLE_CHOICES: { value: RoleChoice; label: string }[] = [
  { value: 'FIELD_CLINICIAN', label: 'Field clinician' },
  { value: 'CLINICAL_SUPERVISOR', label: 'Clinical supervisor' },
  { value: 'OTHER', label: 'Custom' },
];

// Other needs its own wording, which this dialog does not ask for; the server
// refuses it without one.
const EMPLOYMENT_OPTIONS = EMPLOYEE_TYPE_OPTIONS.filter(
  employment => employment.value !== 'OTHER'
);

export interface OnboardUserSubmission {
  email: string;
  first_name: string;
  last_name: string;
  /** The preset, or null for Custom, which names its roles itself. */
  archetype: UserArchetype | null;
  clinic_ids: string[];
  /** Null for a preset, which leaves the agency's configuration to decide. */
  agency_role_ids: string[] | null;
  /** Null while a preset's clinic roles are untouched; the server derives them. */
  clinic_roles: { role_id: string; clinic_ids: string[] }[] | null;
  user_types: UserType[];
  employee_type: EmployeeType;
  roger_doc: {
    mode: OnboardRogerDocMode;
    username?: string;
    account_id?: string;
  };
}

interface OnboardUserDialogProps {
  open: boolean;
  clinics: AgencyClinicResponse[];
  agencyAbbreviation: string | null | undefined;
  /** Every Roger App account in the agency, for spotting an existing one. */
  existingAppAccounts?: readonly ExistingAppAccount[];
  /**
   * Whether this operator may create or link a Roger App account.
   *
   * Creating and linking are gated on `clinician-mgr:access`, which somebody
   * holding only the invite permission does not have. Offering it to them
   * produced a 403 on submit, after they had filled the whole form in.
   */
  allowRogerDocAccounts?: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (submission: OnboardUserSubmission) => Promise<void> | void;
}

const FieldError: React.FC<{ message: string }> = ({ message }) => (
  <p className='mt-1 flex items-start gap-1.5 text-xs text-amber-800'>
    <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
    <span>{message}</span>
  </p>
);

export const OnboardUserDialog: React.FC<OnboardUserDialogProps> = ({
  open,
  clinics,
  agencyAbbreviation,
  existingAppAccounts,
  allowRogerDocAccounts = false,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  // Titles decide visit types, which mean nothing to an agency without the EMR.
  const showEmr = useShowEmrUserManagement();
  // The agency's own flag, kept apart from the one a super user expands: the
  // Roger App offer follows the agency, not the operator.
  const isAgencyEmr = useIsAgencyRogerEmrEnabled();
  const {
    roles,
    effectiveArchetypeRoles,
    isLoading: rolesLoading,
    failed: rolesFailed,
  } = useOnboardRoleContext(open);
  // The roles Custom offers: agency-scoped and non-internal. The server
  // already lists only roles for the agency's products.
  const agencyRoleOptions = useMemo(
    () =>
      filterAssignableAgencyRoles(roles).map(role => ({
        value: role.id,
        label: role.name,
        slug: role.slug,
        description: role.description,
      })),
    [roles]
  );
  // Custom starts from the agency's configured Back office roles, narrowed to
  // the ones the picker shows.
  const customDefaultRoleIds = useMemo(() => {
    const slugs = effectiveArchetypeRoles?.BACK_OFFICE ?? [];
    return agencyRoleOptions
      .filter(option => slugs.includes(option.slug))
      .map(option => option.value);
  }, [agencyRoleOptions, effectiveArchetypeRoles]);
  const {
    form,
    patch,
    reset,
    roleChoice,
    archetype,
    appAccountOffered,
    clinicRolesEdited,
    otherRoleIds,
    disciplines,
    uselessTitles,
    username,
    setEmployeeType,
    setRoleChoice,
    toggleClinicRole,
    toggleClinic,
    effectiveClinicIds,
    toggleTitle,
    blocker: formBlocker,
    existingAccountMatch,
    linkableAccounts,
    linkExistingAccount,
  } = useOnboardUser({
    clinics,
    agencyAbbreviation,
    existingAppAccounts,
    showEmrUserManagement: showEmr,
    isAgencyRogerEmrEnabled: isAgencyEmr,
    customDefaultRoleIds,
  });

  const roleBySlug = useMemo(
    () => new Map(roles.map(role => [role.slug, role])),
    [roles]
  );
  // The catalogue lists only the roles this operator can see and the agency's
  // products use, so a ticked clinic role can be missing; say so, not drop it.
  const sendsClinicRoles = showEmr && (archetype === null || clinicRolesEdited);
  const unresolvedClinicRoles =
    sendsClinicRoles && !rolesLoading && !rolesFailed
      ? CLINIC_ROLE_OPTIONS.filter(
          c =>
            form.clinicRoles[c.value] &&
            !roleBySlug.has(CLINIC_ROLE_SLUGS[c.value])
        ).map(c => c.label)
      : [];
  const blocker =
    formBlocker ??
    (unresolvedClinicRoles.length > 0
      ? {
          field: 'clinicRoles' as const,
          message: `${unresolvedClinicRoles.join(' and ')} can't be granted from here: this agency doesn't use it, or you can't see it. Untick it to continue.`,
        }
      : null);
  const chosenRoleNames = agencyRoleOptions
    .filter(option => otherRoleIds.includes(option.value))
    .map(option => option.label)
    .join(', ');

  const clinicRoles = (): OnboardUserSubmission['clinic_roles'] => {
    if (!showEmr) return [];
    if (archetype !== null && !clinicRolesEdited) return null;
    return CLINIC_ROLE_OPTIONS.filter(c => form.clinicRoles[c.value]).flatMap(
      c => {
        const role = roleBySlug.get(CLINIC_ROLE_SLUGS[c.value]);
        return role
          ? [{ role_id: role.id, clinic_ids: effectiveClinicIds }]
          : [];
      }
    );
  };

  // Cleared whenever it closes, not only when cancelled. The parent closes it
  // directly on success, which bypassed the cancel handler -- so reopening
  // carried the previous person's email, clinics, titles and app-account choice,
  // ready to be sent again under a new name.
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    await onSubmit({
      email: form.email.trim(),
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      archetype,
      clinic_ids: effectiveClinicIds,
      agency_role_ids: archetype === null ? otherRoleIds : null,
      clinic_roles: clinicRoles(),
      user_types: showEmr ? form.userTypes : [],
      employee_type: form.employeeType,
      // Hiding the section is not the boundary: a role switched after it was
      // offered could still carry a stale mode.
      roger_doc:
        !allowRogerDocAccounts ||
        !appAccountOffered ||
        form.appAccountMode === 'none'
          ? { mode: 'none' }
          : form.appAccountMode === 'create'
            ? { mode: 'create', username }
            : { mode: 'link', account_id: form.appAccountId },
    });
  };

  return (
    <Dialog open={open} onOpenChange={value => !value && handleClose()}>
      <DialogContent className='max-h-[90vh] w-full max-w-2xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Add a team member</DialogTitle>
          <DialogDescription>
            They&apos;ll get an email invite to set up their password.
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-col gap-5'>
          {/* ── Who ── two lines: the name, then email and employment. */}
          <div className='flex flex-col gap-3'>
            <div className='flex flex-col gap-3 sm:flex-row'>
              <div className='flex-1'>
                <Label htmlFor='onboard-first'>First name</Label>
                <Input
                  id='onboard-first'
                  inputSize='sm'
                  value={form.firstName}
                  disabled={isSubmitting}
                  onChange={e => patch({ firstName: e.target.value })}
                />
              </div>
              <div className='flex-1'>
                <Label htmlFor='onboard-last'>Last name</Label>
                <Input
                  id='onboard-last'
                  inputSize='sm'
                  value={form.lastName}
                  disabled={isSubmitting}
                  onChange={e => patch({ lastName: e.target.value })}
                />
              </div>
            </div>
            <div className='flex flex-col gap-3 sm:flex-row'>
              <div className='flex-1'>
                <Label htmlFor='onboard-email'>Work email</Label>
                <Input
                  id='onboard-email'
                  inputSize='sm'
                  type='email'
                  value={form.email}
                  disabled={isSubmitting}
                  onChange={e => patch({ email: e.target.value })}
                />
                {blocker?.field === 'email' && (
                  <FieldError message={blocker.message} />
                )}
              </div>
              <div className='flex-1'>
                <Label htmlFor='onboard-employment'>Employment type</Label>
                <Select
                  value={form.employeeType}
                  disabled={isSubmitting}
                  onValueChange={value =>
                    setEmployeeType(value as EmployeeType)
                  }
                >
                  <SelectTrigger id='onboard-employment' selectSize='sm'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_OPTIONS.map(employment => (
                      <SelectItem
                        key={employment.value}
                        value={employment.value}
                      >
                        {employment.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* ── Where ── */}
          <div>
            <Label id='onboard-clinics-label'>Clinic access</Label>
            <div
              role='group'
              aria-labelledby='onboard-clinics-label'
              className='mt-1 grid grid-cols-1 gap-x-4 gap-y-2 rounded-md border p-3 sm:grid-cols-2'
            >
              {clinics.map(clinic => (
                <label
                  key={clinic.id}
                  className='flex min-w-0 items-center gap-2 text-sm'
                  title={clinic.name}
                >
                  <Checkbox
                    checkboxSize='sm'
                    checked={effectiveClinicIds.includes(clinic.id)}
                    disabled={isSubmitting}
                    onCheckedChange={() => toggleClinic(clinic.id)}
                  />
                  <span className='truncate'>{clinic.name}</span>
                </label>
              ))}
            </div>
            {blocker?.field === 'clinics' && (
              <FieldError message={blocker.message} />
            )}
          </div>

          {/* ── Role ── */}
          <div>
            <Label id='onboard-role-label'>
              {showEmr ? 'Role' : 'Agency roles'}
            </Label>
            {showEmr && (
              <div
                role='group'
                aria-labelledby='onboard-role-label'
                className='mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3'
              >
                {ROLE_CHOICES.map(choice => {
                  const selected = roleChoice === choice.value;
                  return (
                    <button
                      key={choice.value}
                      type='button'
                      aria-pressed={selected}
                      disabled={isSubmitting}
                      onClick={() => setRoleChoice(choice.value)}
                      className={`h-9 rounded-md border px-3 text-sm transition-colors ${
                        selected
                          ? 'border-primary bg-primary/5 font-medium ring-1 ring-primary'
                          : 'border-input hover:border-primary'
                      }`}
                    >
                      {choice.label}
                    </button>
                  );
                })}
              </div>
            )}
            {roleChoice === 'OTHER' && (
              // Modal, or the dialog's scroll lock swallows the wheel over the
              // list, which renders outside it.
              <Popover modal>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    aria-label='Agency roles'
                    className='mt-2 w-full justify-between font-normal'
                    disabled={isSubmitting || rolesLoading}
                  >
                    <span
                      className='min-w-0 truncate'
                      title={chosenRoleNames || undefined}
                    >
                      {rolesLoading
                        ? 'Loading roles…'
                        : chosenRoleNames || 'Choose roles'}
                    </span>
                    <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align='start'
                  className='max-h-72 w-[var(--radix-popover-trigger-width)] overflow-y-auto'
                >
                  <MultiSelect
                    options={agencyRoleOptions}
                    value={otherRoleIds}
                    onChange={ids => patch({ otherRoleIds: ids })}
                    idPrefix='onboard-agency-role-'
                    optionRowClassName='gap-3'
                    className='gap-3'
                  />
                </PopoverContent>
              </Popover>
            )}
            {blocker?.field === 'roles' && (
              <FieldError message={blocker.message} />
            )}
            {rolesFailed && (
              <FieldError message='Could not load roles. Close and try again.' />
            )}
          </div>

          {/* ── Clinic roles, at every clinic above ── */}
          {showEmr && (
            <div>
              <Label id='onboard-clinic-roles-label'>
                Clinic roles{' '}
                <span className='font-normal text-muted-foreground'>
                  — at each clinic above
                </span>
              </Label>
              <div
                role='group'
                aria-labelledby='onboard-clinic-roles-label'
                className='mt-1 grid grid-cols-1 gap-x-4 gap-y-2 rounded-md border p-3 sm:grid-cols-3'
              >
                {CLINIC_ROLE_OPTIONS.map(choice => (
                  <label
                    key={choice.value}
                    className='flex items-center gap-2 text-sm'
                  >
                    <Checkbox
                      checkboxSize='sm'
                      checked={form.clinicRoles[choice.value]}
                      disabled={isSubmitting}
                      onCheckedChange={() => toggleClinicRole(choice.value)}
                    />
                    <span className='whitespace-nowrap'>{choice.label}</span>
                  </label>
                ))}
              </div>
              {blocker?.field === 'clinicRoles' && (
                <FieldError message={blocker.message} />
              )}
            </div>
          )}

          {/* ── Titles ── */}
          {showEmr && (
            <div>
              <Label>Clinical title</Label>
              <div className='mt-1 grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border p-3 sm:grid-cols-4'>
                {USER_TYPE_OPTIONS.map(title => (
                  <label
                    key={title.value}
                    className='flex min-w-0 items-center gap-2 text-sm'
                    title={title.label}
                  >
                    <Checkbox
                      checkboxSize='sm'
                      checked={form.userTypes.includes(title.value)}
                      disabled={isSubmitting}
                      onCheckedChange={() => toggleTitle(title.value)}
                    />
                    <span className='truncate'>{title.label}</span>
                  </label>
                ))}
              </div>
              {blocker?.field === 'titles' && (
                <FieldError message={blocker.message} />
              )}
              {disciplines.length > 0 && (
                <div className='mt-3 flex items-start gap-2 rounded-md border border-teal-200 bg-teal-50 p-3 text-xs text-teal-900'>
                  <Info className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                  <span>
                    Disciplines:{' '}
                    <strong>
                      {disciplines.map(disciplineProse).join(' and ')}
                    </strong>{' '}
                    — set from their titles.
                  </span>
                </div>
              )}
              {uselessTitles.length > 0 && (
                <p className='mt-2 flex items-start gap-2 text-xs text-amber-800'>
                  <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                  <span>
                    {uselessTitles.join(', ')} allow no visit types on their
                    own.
                  </span>
                </p>
              )}
            </div>
          )}

          {/* ── Roger App account ── */}
          {appAccountOffered && allowRogerDocAccounts && (
            <div>
              <Label>Roger App account</Label>
              <p className='mt-0.5 text-xs text-muted-foreground'>
                The Roger App uses a different username and password from the
                portal website.
              </p>
              <div className='mt-1 flex flex-col gap-2'>
                <label className='flex items-start gap-2 text-sm'>
                  <input
                    type='radio'
                    className='mt-1'
                    checked={form.appAccountMode === 'create'}
                    disabled={isSubmitting}
                    onChange={() => patch({ appAccountMode: 'create' })}
                  />
                  <span className='flex-1'>
                    <span className='block'>Create a new account</span>
                    {form.appAccountMode === 'create' && (
                      <>
                        <Input
                          inputSize='sm'
                          className='mt-1'
                          aria-label='Roger App username'
                          value={username}
                          disabled={isSubmitting}
                          onChange={e =>
                            patch({
                              username: e.target.value,
                              usernameEdited: true,
                            })
                          }
                        />
                        <span className='mt-1 block text-xs text-muted-foreground'>
                          Filled in from their name and your agency code. Change
                          it if you need to.
                        </span>
                      </>
                    )}
                  </span>
                </label>
                {existingAccountMatch && (
                  <div className='flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900'>
                    <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                    <span>
                      {existingAccountMatch.linkedToEmail ? (
                        <>
                          A similar account,{' '}
                          <strong>
                            {existingAccountMatch.account.username}
                          </strong>
                          , is already linked to{' '}
                          <strong>{existingAccountMatch.linkedToEmail}</strong>{' '}
                          — same person? If so, unlink it from them first. If
                          not, edit the username above — this one is taken.
                        </>
                      ) : (
                        <>
                          <strong>
                            {existingAccountMatch.account.first_name}{' '}
                            {existingAccountMatch.account.last_name}
                          </strong>{' '}
                          already has a Roger App account (
                          <strong>
                            {existingAccountMatch.account.username}
                          </strong>
                          ).{' '}
                          <button
                            type='button'
                            className='font-medium underline'
                            onClick={() =>
                              linkExistingAccount(
                                existingAccountMatch.account.id
                              )
                            }
                          >
                            Link that one instead
                          </button>
                        </>
                      )}
                    </span>
                  </div>
                )}
                <label className='flex items-start gap-2 text-sm'>
                  <input
                    type='radio'
                    className='mt-1'
                    checked={form.appAccountMode === 'link'}
                    disabled={isSubmitting || linkableAccounts.length === 0}
                    onChange={() => patch({ appAccountMode: 'link' })}
                  />
                  <span className='flex-1'>
                    <span className='block'>
                      They already have one — link it
                    </span>
                    {linkableAccounts.length === 0 ? (
                      <span className='mt-0.5 block text-xs text-muted-foreground'>
                        {(existingAppAccounts ?? []).length > 0
                          ? 'Every Roger App account in this agency is already linked to somebody.'
                          : 'No Roger App accounts in this agency yet.'}
                      </span>
                    ) : (
                      form.appAccountMode === 'link' && (
                        <>
                          <Select
                            value={form.appAccountId}
                            disabled={isSubmitting}
                            onValueChange={linkExistingAccount}
                          >
                            <SelectTrigger
                              selectSize='sm'
                              className='mt-1'
                              aria-label='Roger App account to link'
                            >
                              <SelectValue placeholder='Select an account…' />
                            </SelectTrigger>
                            <SelectContent>
                              {(existingAppAccounts ?? []).map(account => (
                                <SelectItem
                                  key={account.id}
                                  value={account.id}
                                  disabled={!!account.linkedToEmail}
                                >
                                  {account.username}
                                  {account.first_name || account.last_name
                                    ? ` — ${[account.first_name, account.last_name].filter(Boolean).join(' ')}`
                                    : ''}
                                  {account.linkedToEmail
                                    ? ` (linked to ${account.linkedToEmail})`
                                    : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className='mt-1 block text-xs text-muted-foreground'>
                            Accounts already linked are shown but cannot be
                            chosen — re-pointing one would break that
                            person&apos;s app login. Unlink it from them first.
                          </span>
                        </>
                      )
                    )}
                  </span>
                </label>
                <label className='flex items-start gap-2 text-sm'>
                  <input
                    type='radio'
                    className='mt-1'
                    checked={form.appAccountMode === 'none'}
                    disabled={isSubmitting}
                    onChange={() => patch({ appAccountMode: 'none' })}
                  />
                  <span>No Roger App account</span>
                </label>
                {blocker?.field === 'appAccount' && (
                  <FieldError message={blocker.message} />
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              isSubmitting || rolesLoading || rolesFailed || blocker !== null
            }
          >
            {isSubmitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardUserDialog;
